import React, { useState, useEffect } from 'react';
import { getPopupSettings, savePopupSettings } from '../services/apiService';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import RandomPopup from '../components/RandomPopup';
import { Clock, Save, CheckCircle2, Sparkles, Gift, Target, TrendingUp, Eye, X, BookOpen, Users, Award, GraduationCap, Globe, Zap, Loader2 } from 'lucide-react';

const AdminPopupSettings = () => {
  const [settings, setSettings] = useState({
    min_interval: 30000,
    max_interval: 90000,
    enabled: true,
    enabled_templates: ['template1'],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const { toast, showToast, hideToast } = useToast();

  // Defines the Look & Feel including specific Button/Text Colors for Admin Preview
  // These must match the structure in RandomPopup.jsx
  const templates = [
    { 
      id: 'template1', 
      name: 'Classic Study Abroad', 
      description: 'Traditional design with global education focus', 
      icon: Sparkles, 
      color: 'from-red-600 to-red-700',
      tag: 'Global Admissions',
      title: "Don't let your dream wait.", 
      subtitle: 'Get a personalized roadmap to top universities.',
      theme: {
        button: 'bg-red-600 hover:bg-red-700',
        ring: 'focus:ring-red-600',
        border: 'focus:border-red-600',
        iconColor: 'text-red-600',
        lightBg: 'bg-red-50'
      }
    },
    { 
      id: 'template2', 
      name: 'Success Story', 
      description: 'Highlight student achievements and visa success', 
      icon: Gift, 
      color: 'from-blue-600 to-blue-700',
      tag: 'Success Story',
      title: 'Join 5,000+ Successful Students', 
      subtitle: 'Your success story starts here.',
      theme: {
        button: 'bg-blue-600 hover:bg-blue-700',
        ring: 'focus:ring-blue-600',
        border: 'focus:border-blue-600',
        iconColor: 'text-blue-600',
        lightBg: 'bg-blue-50'
      }
    },
    { 
      id: 'template3', 
      name: 'Course Promotion', 
      description: 'Focus on IELTS and language training courses', 
      icon: Target, 
      color: 'from-purple-600 to-purple-700',
      tag: 'IELTS Masterclass',
      title: 'Score Your Target Band', 
      subtitle: 'Crack IELTS with 8+ Band Score',
      theme: {
        button: 'bg-purple-600 hover:bg-purple-700',
        ring: 'focus:ring-purple-600',
        border: 'focus:border-purple-600',
        iconColor: 'text-purple-600',
        lightBg: 'bg-purple-50'
      }
    },
    { 
      id: 'template4', 
      name: 'Premium Offer', 
      description: 'Exclusive offers and limited-time promotions', 
      icon: TrendingUp, 
      color: 'from-emerald-600 to-emerald-700',
      tag: 'Limited Time',
      title: 'Exclusive Offer', 
      subtitle: 'Free Profile Evaluation + Scholarship Guidance',
      theme: {
        button: 'bg-emerald-600 hover:bg-emerald-700',
        ring: 'focus:ring-emerald-600',
        border: 'focus:border-emerald-600',
        iconColor: 'text-emerald-600',
        lightBg: 'bg-emerald-50'
      }
    },
    { 
      id: 'template5', 
      name: 'IELTS Excellence', 
      description: 'Focus on IELTS training and band score achievements', 
      icon: BookOpen, 
      color: 'from-orange-600 to-orange-700',
      tag: 'IELTS Training',
      title: 'Achieve Your Target Band', 
      subtitle: 'Join 2,000+ students who achieved their dream score.',
      theme: {
        button: 'bg-orange-600 hover:bg-orange-700',
        ring: 'focus:ring-orange-600',
        border: 'focus:border-orange-600',
        iconColor: 'text-orange-600',
        lightBg: 'bg-orange-50'
      }
    },
    { 
      id: 'template6', 
      name: 'Student Community', 
      description: 'Join thousands of successful students worldwide', 
      icon: Users, 
      color: 'from-indigo-600 to-indigo-700',
      tag: 'Community',
      title: 'Join 10,000+ Students', 
      subtitle: "Be part of India's largest study abroad community.",
      theme: {
        button: 'bg-indigo-600 hover:bg-indigo-700',
        ring: 'focus:ring-indigo-600',
        border: 'focus:border-indigo-600',
        iconColor: 'text-indigo-600',
        lightBg: 'bg-indigo-50'
      }
    },
    { 
      id: 'template7', 
      name: 'Scholarship Guide', 
      description: 'Discover scholarship opportunities and funding options', 
      icon: Award, 
      color: 'from-pink-600 to-pink-700',
      tag: 'Scholarships',
      title: 'Unlock Scholarships Worth ₹50L+', 
      subtitle: 'Our students have secured over ₹5 Crores.',
      theme: {
        button: 'bg-pink-600 hover:bg-pink-700',
        ring: 'focus:ring-pink-600',
        border: 'focus:border-pink-600',
        iconColor: 'text-pink-600',
        lightBg: 'bg-pink-50'
      }
    },
    { 
      id: 'template8', 
      name: 'University Partners', 
      description: 'Direct admission to top-ranked universities', 
      icon: GraduationCap, 
      color: 'from-teal-600 to-teal-700',
      tag: 'Partner Universities',
      title: 'Direct Admission', 
      subtitle: 'Get priority access to top-ranked institutions.',
      theme: {
        button: 'bg-teal-600 hover:bg-teal-700',
        ring: 'focus:ring-teal-600',
        border: 'focus:border-teal-600',
        iconColor: 'text-teal-600',
        lightBg: 'bg-teal-50'
      }
    },
    { 
      id: 'template9', 
      name: 'Global Opportunities', 
      description: 'Study in UK, Canada, Australia, and more', 
      icon: Globe, 
      color: 'from-cyan-600 to-cyan-700',
      tag: 'Global Study',
      title: 'Study in 20+ Countries', 
      subtitle: 'Explore endless opportunities across the globe.',
      theme: {
        button: 'bg-cyan-600 hover:bg-cyan-700',
        ring: 'focus:ring-cyan-600',
        border: 'focus:border-cyan-600',
        iconColor: 'text-cyan-600',
        lightBg: 'bg-cyan-50'
      }
    },
    { 
      id: 'template10', 
      name: 'Quick Start', 
      description: 'Fast-track your study abroad journey', 
      icon: Zap, 
      color: 'from-amber-600 to-amber-700',
      tag: 'Quick Start',
      title: 'Start Your Journey Today', 
      subtitle: 'Get started in minutes, not weeks.',
      theme: {
        button: 'bg-amber-600 hover:bg-amber-700',
        ring: 'focus:ring-amber-600',
        border: 'focus:border-amber-600',
        iconColor: 'text-amber-600',
        lightBg: 'bg-amber-50'
      }
    },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await getPopupSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getMinIntervalSeconds = () => Math.floor((settings.min_interval || 30000) / 1000);
  const getMaxIntervalSeconds = () => Math.floor((settings.max_interval || 90000) / 1000);

  const handleIntervalChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    setSettings({ ...settings, [field]: numValue * 1000 });
  };

  const handleTemplateToggle = (templateId) => {
    const enabled = settings.enabled_templates || [];
    if (enabled.includes(templateId)) {
      if (enabled.length > 1) {
        setSettings({ ...settings, enabled_templates: enabled.filter(id => id !== templateId) });
      } else {
        showToast('At least one template must be enabled', 'error');
      }
    } else {
      setSettings({ ...settings, enabled_templates: [...enabled, templateId] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (settings.min_interval >= settings.max_interval) {
      showToast('Min interval must be less than Max interval', 'error');
      return;
    }

    try {
      setSaving(true);
      await savePopupSettings(settings);
      showToast('Settings saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Error saving settings. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-red-600 mb-4"></div>
        <p className="text-slate-500">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 pb-20 md:pb-0">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Popup Settings</h1>
            <p className="text-slate-500 text-xs md:text-sm mt-1">Configure random popup behavior.</p>
        </div>
        <button
            type="submit"
            form="popupSettingsForm"
            disabled={saving}
            className="hidden md:flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20 disabled:opacity-50"
        >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            Save Changes
        </button>
      </div>

      <form id="popupSettingsForm" onSubmit={handleSubmit} className="space-y-6">
        
        {/* Enable/Disable Popup */}
        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="text-red-600" size={20} />
            <h2 className="text-lg font-bold text-slate-900">Popup Status</h2>
          </div>
          
          <label className="flex items-center gap-3 cursor-pointer p-2 hover:bg-slate-50 rounded-lg transition-colors -ml-2">
            <div className="relative">
                <input
                    type="checkbox"
                    checked={settings.enabled !== false}
                    onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                    className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </div>
            <span className="text-sm font-semibold text-slate-900">Enable random popup on website</span>
          </label>
        </div>

        {/* Time Interval Settings */}
        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="text-red-600" size={20} />
            <h2 className="text-lg font-bold text-slate-900">Display Interval</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                Minimum Interval (seconds)
              </label>
              <input
                type="number"
                value={getMinIntervalSeconds()}
                onChange={(e) => handleIntervalChange('min_interval', e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                min="10"
                step="1"
                required
              />
              <p className="text-[10px] text-slate-400 mt-1">Minimum: 10 seconds</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                Maximum Interval (seconds)
              </label>
              <input
                type="number"
                value={getMaxIntervalSeconds()}
                onChange={(e) => handleIntervalChange('max_interval', e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                min="30"
                step="1"
                required
              />
              <p className="text-[10px] text-slate-400 mt-1">Recommended: 30 - 120 seconds</p>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100 flex items-start gap-2">
            <div className="mt-0.5 text-blue-500"><Zap size={14} /></div>
            <div>
                <p className="text-xs text-blue-800 font-medium">
                Active Range: {getMinIntervalSeconds()}s - {getMaxIntervalSeconds()}s
                </p>
                <p className="text-[10px] text-blue-600 mt-0.5">
                Popup will appear randomly within this timeframe.
                </p>
            </div>
          </div>
        </div>

        {/* Template Selection */}
        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 mb-2">Popup Templates</h2>
          <p className="text-xs text-slate-500 mb-4">
            Select active templates. One will be chosen randomly.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {templates.map((template) => {
              const Icon = template.icon;
              const isEnabled = (settings.enabled_templates || []).includes(template.id);
              
              // Extract the base color name (e.g., 'red', 'blue') from the color string 'from-red-600...'
              const colorName = template.color.split('-')[1];

              return (
                <div
                  key={template.id}
                  onClick={() => handleTemplateToggle(template.id)}
                  className={`relative p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    isEnabled
                      ? `border-${colorName}-600 bg-${colorName}-50`
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${template.color} flex items-center justify-center text-white flex-shrink-0`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                          <h3 className="font-bold text-slate-900 text-sm truncate pr-2">{template.name}</h3>
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            isEnabled ? `border-${colorName}-600 bg-${colorName}-600` : 'border-slate-300'
                          }`}>
                            {isEnabled && <CheckCircle2 size={12} className="text-white" />}
                          </div>
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-1">{template.description}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewTemplate(template.id);
                    }}
                    className="absolute bottom-2 right-2 p-1.5 bg-white border border-slate-200 rounded-full text-slate-400 hover:text-blue-600 hover:border-blue-200 shadow-sm z-10"
                    title="Preview"
                  >
                    <Eye size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </form>

      {/* Mobile Sticky Save Button */}
      <div className="bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 md:hidden z-40">
        <button
            type="submit"
            form="popupSettingsForm"
            disabled={saving}
            className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg"
        >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            Save Changes
        </button>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} duration={toast.duration} onClose={hideToast} />}

      {/* LIVE FULL SCREEN PREVIEW */}
      {previewTemplate && (
        <div className="fixed inset-0 z-[100] animate-in fade-in duration-200">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"></div>
            
            {/* Render the actual Popup Component, passing the selected template configuration */}
            <RandomPopup 
                onClose={() => setPreviewTemplate(null)} 
                templateId={previewTemplate}
                customTemplateData={templates.find(t => t.id === previewTemplate)} // Pass the full template object with theme
                isPreview={true} 
                forceOpen={true} 
            />

            {/* Admin Exit Button */}
            <div className="fixed top-0 left-0 right-0 z-[110] p-4 flex justify-center pointer-events-none">
                <div className="pointer-events-auto bg-slate-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-300 border-r border-slate-700 pr-3 mr-1">Preview Mode</span>
                    <button onClick={() => setPreviewTemplate(null)} className="flex items-center gap-2 hover:text-red-400 transition-colors font-bold text-sm">
                        <X size={18} /> Close Preview
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default AdminPopupSettings;